/** Shared constants + helpers for Z Report 1 (Excel layout). */

export const Z1_BANK_ACCOUNTS = ['HBL-1403', 'Faysal-Flink', 'PA-4823'];

/** Old column keys → current (keeps saved Z Report bank values after rename). */
const Z1_BANK_ACCOUNT_ALIASES = {
  'HBL-2403': 'Faysal-Flink',
};

export const Z1_SALE_ITEMS = [
  { id: 'new_membership', label: 'New Membership' },
  { id: 'renewal', label: 'Renewal Sale' },
  { id: 'pt_sale', label: 'Pt Sale' },
  { id: 'guest_pass', label: 'Guest Pass' },
  { id: 'locker_sale', label: 'Locker Sale' },
  { id: 'spa_sale', label: 'Spa Sale' },
  { id: 'oxybar', label: 'Oxybar Sale' },
  { id: 'zumba_sale', label: 'Zumba Sale' },
  { id: 'yoga_sale', label: 'Yoga Sale' },
  { id: 'gym_apparel', label: 'Gym Apparel' },
  { id: 'miscellaneous_sale', label: 'Misc Sale' },
  { id: 'remaining_clear', label: 'Remaining Clear' },
];

export const Z1_PAYMENT_ITEMS = [
  { id: 'cash', label: 'Cash' },
  { id: 'card', label: 'Credit Card' },
  { id: 'bank', label: 'Bank Transfer' },
  { id: 'qr', label: 'Qr' },
];

export const Z1_ACTIVE_ITEMS = [
  { id: 'membership_sale_month', label: 'Membership During Month' },
  { id: 'pt_sale_month', label: 'Pt During Month' },
  { id: 'active_women', label: 'Active Member Women' },
  { id: 'active_men', label: 'Active Member Men' },
];

function emptyBankColumn() {
  return {
    opening: '',
    received: '',
    sale: '',
    pearlGroupExpenses: '',
    gymExpenses: '',
  };
}

/** Empty bank summary grid for all fixed accounts (shared by Z Report + Z Report 1). */
export function emptyZBankAccounts() {
  return Object.fromEntries(Z1_BANK_ACCOUNTS.map((acc) => [acc, emptyBankColumn()]));
}

function remoteBankColumn(remoteAccounts, acc) {
  if (!remoteAccounts || typeof remoteAccounts !== 'object') return {};
  if (remoteAccounts[acc]) return remoteAccounts[acc] || {};
  const legacyKey = Object.keys(Z1_BANK_ACCOUNT_ALIASES).find(
    (old) => Z1_BANK_ACCOUNT_ALIASES[old] === acc,
  );
  return (legacyKey && remoteAccounts[legacyKey]) || {};
}

/** Merge saved bank_accounts JSON with legacy single-column Z Report fields. */
export function mergeZBankAccounts(remoteAccounts, legacy = {}) {
  const next = emptyZBankAccounts();
  const hasRemote = remoteAccounts
    && typeof remoteAccounts === 'object'
    && (
      Z1_BANK_ACCOUNTS.some((acc) => remoteAccounts[acc])
      || Object.keys(Z1_BANK_ACCOUNT_ALIASES).some((old) => remoteAccounts[old])
    );
  if (hasRemote) {
    Z1_BANK_ACCOUNTS.forEach((acc) => {
      const col = remoteBankColumn(remoteAccounts, acc);
      next[acc] = {
        opening: col.opening != null && col.opening !== '' ? String(col.opening) : '',
        received: col.received != null && col.received !== '' ? String(col.received) : '',
        sale: col.sale != null && col.sale !== '' ? String(col.sale) : '',
        pearlGroupExpenses: col.pearlGroupExpenses != null && col.pearlGroupExpenses !== ''
          ? String(col.pearlGroupExpenses)
          : (col.pearl_group_expenses != null ? String(col.pearl_group_expenses) : ''),
        gymExpenses: col.gymExpenses != null && col.gymExpenses !== ''
          ? String(col.gymExpenses)
          : (col.gym_expenses != null ? String(col.gym_expenses) : ''),
      };
    });
    return next;
  }
  const first = Z1_BANK_ACCOUNTS[0];
  if (first) {
    if (legacy.openingBank != null && legacy.openingBank !== '') {
      next[first].opening = String(legacy.openingBank);
    }
    if (legacy.bankReceived != null && legacy.bankReceived !== '') {
      next[first].received = String(legacy.bankReceived);
    }
    if (legacy.bankSale != null && legacy.bankSale !== '') {
      next[first].sale = String(legacy.bankSale);
    }
    if (legacy.pearlGroupBank != null && legacy.pearlGroupBank !== '') {
      next[first].pearlGroupExpenses = String(legacy.pearlGroupBank);
    }
  }
  return next;
}

export function sumZBankField(bankAccounts, field) {
  return Z1_BANK_ACCOUNTS.reduce(
    (sum, acc) => sum + (Number(bankAccounts?.[acc]?.[field]) || 0),
    0,
  );
}

export function buildBankAccountsSavePayload(bankAccounts, closingBanks) {
  const toNum = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return 0;
    return Math.max(0, Math.round(Number(s.replace(/,/g, '')) || 0));
  };
  return Object.fromEntries(
    Z1_BANK_ACCOUNTS.map((acc) => {
      const col = bankAccounts?.[acc] || {};
      return [acc, {
        opening: toNum(col.opening),
        received: toNum(col.received),
        sale: toNum(col.sale),
        pearlGroupExpenses: toNum(col.pearlGroupExpenses),
        gymExpenses: toNum(col.gymExpenses),
        closing: Math.round(Number(closingBanks?.[acc]) || 0),
      }];
    }),
  );
}

function emptyCountAmountBlock(ids) {
  return Object.fromEntries(ids.map((id) => [id, { count: '', amount: '' }]));
}

function emptyAmountBlock(ids) {
  return Object.fromEntries(ids.map((id) => [id, '']));
}

const saleIds = Z1_SALE_ITEMS.map((r) => r.id);
const paymentIds = Z1_PAYMENT_ITEMS.map((r) => r.id);
const activeIds = Z1_ACTIVE_ITEMS.map((r) => r.id);

export function emptyZReport1Manual() {
  return {
    bankAccounts: emptyZBankAccounts(),
    cashSummary: {
      opening: '',
      received: '',
      todayCashSale: '',
      pearlGroupExpenses: '',
      gymExpenses: '',
    },
    todaySaleSummary: emptyCountAmountBlock(saleIds),
    monthSaleSummary: emptyAmountBlock(saleIds),
    todayPaymentMethod: emptyCountAmountBlock(paymentIds),
    monthPaymentMethod: emptyAmountBlock(paymentIds),
    totalActiveMembers: emptyAmountBlock(activeIds),
    totalExpenseSummary: {
      gym_cash: '',
      gym_bank: '',
      pearl_cash: '',
      pearl_bank: '',
    },
  };
}

export function cloneZReport1Manual(manual) {
  const blank = emptyZReport1Manual();
  return {
    bankAccounts: Object.fromEntries(
      Z1_BANK_ACCOUNTS.map((acc) => [
        acc,
        { ...blank.bankAccounts[acc], ...(manual.bankAccounts?.[acc] || {}) },
      ]),
    ),
    cashSummary: { ...blank.cashSummary, ...(manual.cashSummary || {}) },
    todaySaleSummary: Object.fromEntries(
      saleIds.map((id) => [
        id,
        { ...blank.todaySaleSummary[id], ...(manual.todaySaleSummary?.[id] || {}) },
      ]),
    ),
    monthSaleSummary: Object.fromEntries(
      saleIds.map((id) => [id, manual.monthSaleSummary?.[id] ?? '']),
    ),
    todayPaymentMethod: Object.fromEntries(
      paymentIds.map((id) => [
        id,
        { ...blank.todayPaymentMethod[id], ...(manual.todayPaymentMethod?.[id] || {}) },
      ]),
    ),
    monthPaymentMethod: Object.fromEntries(
      paymentIds.map((id) => [id, manual.monthPaymentMethod?.[id] ?? '']),
    ),
    totalActiveMembers: Object.fromEntries(
      activeIds.map((id) => [id, manual.totalActiveMembers?.[id] ?? '']),
    ),
    totalExpenseSummary: {
      ...blank.totalExpenseSummary,
      ...(manual.totalExpenseSummary || {}),
    },
  };
}

function strVal(value) {
  if (value === 0 || value) return String(value);
  return '';
}

function strCountAmount(value) {
  if (!value || typeof value !== 'object') return { count: '', amount: '' };
  return {
    count: strVal(value.count),
    amount: strVal(value.amount),
  };
}

/** Map saved JSON → editable form strings. */
export function remoteToZReport1Manual(remotePayload) {
  const blank = emptyZReport1Manual();
  if (!remotePayload || typeof remotePayload !== 'object') return blank;

  const bankAccounts = Object.fromEntries(
    Z1_BANK_ACCOUNTS.map((acc) => {
      const src = remotePayload.bankAccounts?.[acc] || {};
      return [acc, {
        opening: strVal(src.opening),
        received: strVal(src.received),
        sale: strVal(src.sale),
        pearlGroupExpenses: strVal(src.pearlGroupExpenses),
        gymExpenses: strVal(src.gymExpenses),
      }];
    }),
  );

  const cash = remotePayload.cashSummary || {};
  return {
    bankAccounts,
    cashSummary: {
      opening: strVal(cash.opening),
      received: strVal(cash.received),
      todayCashSale: strVal(cash.todayCashSale),
      pearlGroupExpenses: strVal(cash.pearlGroupExpenses),
      gymExpenses: strVal(cash.gymExpenses),
    },
    todaySaleSummary: Object.fromEntries(
      saleIds.map((id) => [id, strCountAmount(remotePayload.todaySaleSummary?.[id])]),
    ),
    monthSaleSummary: Object.fromEntries(
      saleIds.map((id) => [id, strVal(remotePayload.monthSaleSummary?.[id])]),
    ),
    todayPaymentMethod: Object.fromEntries(
      paymentIds.map((id) => [id, strCountAmount(remotePayload.todayPaymentMethod?.[id])]),
    ),
    monthPaymentMethod: Object.fromEntries(
      paymentIds.map((id) => [id, strVal(remotePayload.monthPaymentMethod?.[id])]),
    ),
    totalActiveMembers: Object.fromEntries(
      activeIds.map((id) => [id, strVal(remotePayload.totalActiveMembers?.[id])]),
    ),
    totalExpenseSummary: {
      gym_cash: strVal(remotePayload.totalExpenseSummary?.gym_cash),
      gym_bank: strVal(remotePayload.totalExpenseSummary?.gym_bank),
      pearl_cash: strVal(remotePayload.totalExpenseSummary?.pearl_cash),
      pearl_bank: strVal(remotePayload.totalExpenseSummary?.pearl_bank),
    },
  };
}

export function numOrBlank(value) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function calcZ1Closing(opening, received, sale, pearl, gym) {
  return Math.round(
    (Number(opening) || 0)
    + (Number(received) || 0)
    + (Number(sale) || 0)
    - (Number(pearl) || 0)
    - (Number(gym) || 0),
  );
}

export function computeZBankClosings(bankAccounts) {
  const closingBanks = Object.fromEntries(
    Z1_BANK_ACCOUNTS.map((acc) => {
      const col = bankAccounts?.[acc] || {};
      return [acc, calcZ1Closing(
        col.opening,
        col.received,
        col.sale,
        col.pearlGroupExpenses,
        col.gymExpenses,
      )];
    }),
  );
  const closingBank = Z1_BANK_ACCOUNTS.reduce(
    (sum, acc) => sum + (Number(closingBanks[acc]) || 0),
    0,
  );
  return { closingBanks, closingBank };
}

export function computeZ1Closings(manual, autoTodayCashSale = 0) {
  const cashSale = String(manual.cashSummary?.todayCashSale ?? '').trim() === ''
    ? Math.round(Number(autoTodayCashSale) || 0)
    : Math.round(Number(manual.cashSummary.todayCashSale) || 0);

  const closingCash = calcZ1Closing(
    manual.cashSummary?.opening,
    manual.cashSummary?.received,
    cashSale,
    manual.cashSummary?.pearlGroupExpenses,
    manual.cashSummary?.gymExpenses,
  );

  const closingBanks = Object.fromEntries(
    Z1_BANK_ACCOUNTS.map((acc) => {
      const col = manual.bankAccounts?.[acc] || {};
      return [acc, calcZ1Closing(
        col.opening,
        col.received,
        col.sale,
        col.pearlGroupExpenses,
        col.gymExpenses,
      )];
    }),
  );

  return { closingCash, closingBanks, cashSaleUsed: cashSale };
}

export function applyTodayAutoToManual(manual, auto) {
  if (!auto) return manual;
  const next = cloneZReport1Manual(manual);

  if (String(next.cashSummary.todayCashSale ?? '').trim() === '') {
    next.cashSummary.todayCashSale = String(Math.round(Number(auto.cashSale) || 0));
  }

  (auto.saleSummary || []).forEach((row) => {
    const id = row.id;
    if (!next.todaySaleSummary[id]) return;
    if (String(next.todaySaleSummary[id].count ?? '').trim() === '') {
      next.todaySaleSummary[id].count = String(Number(row.count) || 0);
    }
    if (String(next.todaySaleSummary[id].amount ?? '').trim() === '') {
      next.todaySaleSummary[id].amount = String(Math.round(Number(row.value) || 0));
    }
  });

  (auto.paymentMethods || []).forEach((row) => {
    const id = row.id;
    if (!next.todayPaymentMethod[id]) return;
    if (String(next.todayPaymentMethod[id].count ?? '').trim() === '') {
      next.todayPaymentMethod[id].count = String(Number(row.count) || 0);
    }
    if (String(next.todayPaymentMethod[id].amount ?? '').trim() === '') {
      next.todayPaymentMethod[id].amount = String(Math.round(Number(row.amount) || 0));
    }
  });

  return next;
}

export function applyActiveMemberSeed(manual, stats) {
  if (!stats) return manual;
  const next = cloneZReport1Manual(manual);
  const map = {
    membership_sale_month: stats.membership_sale_month,
    pt_sale_month: stats.pt_sale_month,
    active_women: stats.active_women,
    active_men: stats.active_men,
  };
  Object.entries(map).forEach(([key, value]) => {
    if (String(next.totalActiveMembers[key] ?? '').trim() === '' && (value === 0 || value)) {
      next.totalActiveMembers[key] = String(Math.round(Number(value) || 0));
    }
  });
  return next;
}

export function applyOpeningSeedFromPrevious(manual, prevPayload) {
  if (!prevPayload) return manual;
  const next = cloneZReport1Manual(manual);

  if (String(next.cashSummary.opening ?? '').trim() === '') {
    next.cashSummary.opening = String(Math.round(Number(prevPayload.closingCash) || 0));
  }

  Z1_BANK_ACCOUNTS.forEach((acc) => {
    if (String(next.bankAccounts[acc].opening ?? '').trim() === '') {
      const closing = prevPayload.closingBanks?.[acc];
      next.bankAccounts[acc].opening = String(Math.round(Number(closing) || 0));
    }
  });

  return next;
}

export function buildZReport1SavePayload(manual, closings) {
  const toNum = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return 0;
    return Math.max(0, Math.round(Number(s.replace(/,/g, '')) || 0));
  };

  const bankAccounts = Object.fromEntries(
    Z1_BANK_ACCOUNTS.map((acc) => {
      const col = manual.bankAccounts?.[acc] || {};
      return [acc, {
        opening: toNum(col.opening),
        received: toNum(col.received),
        sale: toNum(col.sale),
        pearlGroupExpenses: toNum(col.pearlGroupExpenses),
        gymExpenses: toNum(col.gymExpenses),
        closing: closings.closingBanks[acc] ?? 0,
      }];
    }),
  );

  const todaySaleSummary = Object.fromEntries(
    saleIds.map((id) => {
      const row = manual.todaySaleSummary?.[id] || {};
      return [id, {
        count: toNum(row.count),
        amount: toNum(row.amount),
      }];
    }),
  );

  const todayPaymentMethod = Object.fromEntries(
    paymentIds.map((id) => {
      const row = manual.todayPaymentMethod?.[id] || {};
      return [id, {
        count: toNum(row.count),
        amount: toNum(row.amount),
      }];
    }),
  );

  return {
    bankAccounts,
    cashSummary: {
      opening: toNum(manual.cashSummary?.opening),
      received: toNum(manual.cashSummary?.received),
      todayCashSale: closings.cashSaleUsed ?? 0,
      pearlGroupExpenses: toNum(manual.cashSummary?.pearlGroupExpenses),
      gymExpenses: toNum(manual.cashSummary?.gymExpenses),
      closing: closings.closingCash ?? 0,
    },
    todaySaleSummary,
    monthSaleSummary: Object.fromEntries(
      saleIds.map((id) => [id, toNum(manual.monthSaleSummary?.[id])]),
    ),
    todayPaymentMethod,
    monthPaymentMethod: Object.fromEntries(
      paymentIds.map((id) => [id, toNum(manual.monthPaymentMethod?.[id])]),
    ),
    totalActiveMembers: Object.fromEntries(
      activeIds.map((id) => [id, toNum(manual.totalActiveMembers?.[id])]),
    ),
    totalExpenseSummary: {
      gym_cash: toNum(manual.totalExpenseSummary?.gym_cash),
      gym_bank: toNum(manual.totalExpenseSummary?.gym_bank),
      pearl_cash: toNum(manual.totalExpenseSummary?.pearl_cash),
      pearl_bank: toNum(manual.totalExpenseSummary?.pearl_bank),
    },
    closingCash: closings.closingCash ?? 0,
    closingBanks: closings.closingBanks ?? {},
  };
}

export function sumTodaySale(manual) {
  return Z1_SALE_ITEMS.reduce(
    (sum, row) => sum + (Number(manual.todaySaleSummary?.[row.id]?.amount) || 0),
    0,
  );
}

export function sumTodaySaleCount(manual) {
  return Z1_SALE_ITEMS.reduce(
    (sum, row) => sum + (Number(manual.todaySaleSummary?.[row.id]?.count) || 0),
    0,
  );
}

export function sumMonthSale(manual) {
  return Z1_SALE_ITEMS.reduce(
    (sum, row) => sum + (Number(manual.monthSaleSummary?.[row.id]) || 0),
    0,
  );
}

export function sumTodayPayment(manual) {
  return Z1_PAYMENT_ITEMS.reduce(
    (sum, row) => sum + (Number(manual.todayPaymentMethod?.[row.id]?.amount) || 0),
    0,
  );
}

export function sumTodayPaymentCount(manual) {
  return Z1_PAYMENT_ITEMS.reduce(
    (sum, row) => sum + (Number(manual.todayPaymentMethod?.[row.id]?.count) || 0),
    0,
  );
}

export function sumMonthPayment(manual) {
  return Z1_PAYMENT_ITEMS.reduce(
    (sum, row) => sum + (Number(manual.monthPaymentMethod?.[row.id]) || 0),
    0,
  );
}

export function sumExpenseGym(manual) {
  return (Number(manual.totalExpenseSummary?.gym_cash) || 0)
    + (Number(manual.totalExpenseSummary?.gym_bank) || 0);
}

export function sumExpensePearl(manual) {
  return (Number(manual.totalExpenseSummary?.pearl_cash) || 0)
    + (Number(manual.totalExpenseSummary?.pearl_bank) || 0);
}
